import { usersApi } from "@/services/backendApi";

let cache = null;
let pending = null;

function trim(value) {
  return String(value || "").trim();
}

export function mentionLabel(user) {
  const first = trim(user?.firstName);
  const nick = trim(user?.nickname);
  if (first && nick) return `${first} (${nick})`;
  if (first) return first;
  if (nick) return nick;
  return trim(user?.username) || `user${user?.userId || ""}`;
}

export function mentionToken(user) {
  return `@[${mentionLabel(user)}]`;
}

export function mentionSearchText(user) {
  return [
    user?.firstName,
    user?.lastName,
    user?.nickname,
    user?.username,
    mentionLabel(user),
  ]
    .map((v) => trim(v).toLowerCase())
    .filter(Boolean)
    .join(" ");
}

export async function loadMentionUsers() {
  if (cache) return cache;
  if (pending) return pending;
  pending = usersApi
    .getAll()
    .then((rows) => {
      cache = (Array.isArray(rows) ? rows : [])
        .map((u) => ({
          userId: u.userId,
          firstName: u.firstName,
          lastName: u.lastName,
          nickname: u.nickname,
          username: u.username,
          label: mentionLabel(u),
          token: mentionToken(u),
          search: mentionSearchText(u),
        }))
        .filter((u) => u.userId && u.label);
      return cache;
    })
    .catch((err) => {
      console.error("loadMentionUsers error:", err);
      cache = [];
      return cache;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

export function getMentionQuery(value, cursor) {
  const text = String(value || "");
  const pos = Math.max(0, Math.min(cursor == null ? text.length : cursor, text.length));
  const before = text.slice(0, pos);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before.charAt(at - 1))) return null;
  const raw = before.slice(at + 1);
  if (raw.startsWith("[")) {
    if (raw.includes("]")) return null;
    return { start: at, query: raw.slice(1) };
  }
  if (/\s/.test(raw)) return null;
  return { start: at, query: raw };
}

export function filterMentionUsers(users, query) {
  const q = String(query || "").trim().toLowerCase();
  const list = Array.isArray(users) ? users : [];
  if (!q) return list.slice(0, 8);
  return list.filter((u) => u.search.includes(q)).slice(0, 8);
}

export function insertMention(value, cursor, start, user) {
  const text = String(value || "");
  const pos = Math.max(0, Math.min(cursor == null ? text.length : cursor, text.length));
  const token = `${user.token} `;
  const next = `${text.slice(0, start)}${token}${text.slice(pos)}`;
  return { value: next, cursor: start + token.length };
}

const MENTION_RE = /@\[[^\]]+\]|@[A-Za-z0-9._-]+/g;

export function splitMentionParts(text) {
  const s = String(text ?? "");
  const parts = [];
  let last = 0;
  MENTION_RE.lastIndex = 0;
  let match = MENTION_RE.exec(s);
  while (match) {
    if (match.index > last) parts.push({ type: "text", text: s.slice(last, match.index) });
    parts.push({ type: "mention", text: match[0] });
    last = match.index + match[0].length;
    match = MENTION_RE.exec(s);
  }
  if (last < s.length) parts.push({ type: "text", text: s.slice(last) });
  return parts.length ? parts : [{ type: "text", text: s }];
}
